import { Component, OnDestroy, ViewChild } from '@angular/core'
import {
  Currency,
  Depot,
  File,
  Payment,
  Product,
  ProductMove,
  ProductMovePack,
  PurchaseOrder,
  PurchaseOrderItem,
} from 'depoto-core/src/entities'
import { CoreService, HsApiService, NotificationService } from '../../services'
import { Supplier, Tag } from 'depoto-core/src/entities'
import { ListingItem } from 'depoto-core/src/models'
import { productMovePacksPurchaseOrderColumns } from '../../utils/definitions.util'
import { ActivatedRoute, Router } from '@angular/router'
import { FmtDatePipe } from '../../pipes'
import { roundNum, SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { BaseListing } from '../_base-listing/base-listing'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { ModalQrCodeComponent } from '../../components/modal-components/modal-qrcode/modal-qrcode.component'
import { TranslateService } from '@ngx-translate/core'
import { Address, PaymentType } from 'depoto-core'
import { ModalAddressCreateComponent } from '../../components/modal-components/modal-address-create/modal-address-create.component'
import {
  FREE_SHIPPING_THRESHOLD_CZK,
  FREE_SHIPPING_THRESHOLD_EUR,
  SHIPPING_PRICE_CZK,
  SHIPPING_PRICE_EUR,
} from '../product-purchase-order-creation-hs/product-purchase-order-creation-hs.component'

@Component({
  selector: 'app-purchase-order-checkout',
  templateUrl: './purchase-order-checkout.component.html',
  styleUrls: ['./purchase-order-checkout.component.scss'],
  providers: [FmtDatePipe],
})
export class PurchaseOrderCheckoutComponent implements CheckDataChanges {
  @ViewChild('modalQr') modalQr: ModalQrCodeComponent
  @ViewChild('modalAddressCreate') modalAddressCreate: ModalAddressCreateComponent
  suppliers: Supplier[] = []
  loading = true
  reloading = false
  public isCreationModeEnabled = true
  public isEditModeEnabled = true
  public canEdit = false
  public productList: Array<any> = []
  purchaseOrderInfo: Partial<PurchaseOrderInfo> = {}
  public purchaseOrder: Partial<PurchaseOrder> | any | undefined
  readonly purchaseOrderId
  priceSum = 0
  priceWithVatSum = 0
  discountsSum = 0
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this
  activeTab: 'common' | 'pmps' = 'common'
  hasUnsavedChanges = false
  objectForChangesCheck: string
  freeShippingThreshold = FREE_SHIPPING_THRESHOLD_CZK
  shippingPrice = SHIPPING_PRICE_CZK

  customerCode: string

  productMovePacks: ProductMovePack[] = []
  listingColumns: ListingItem[] = productMovePacksPurchaseOrderColumns
  storageKey: { type: string; key: string }
  sortProp: 'created'
  sortDir: 'desc'

  // checkout props:
  payments = [
    { id: 'invoice', name: 'Faktura' },
    { id: 'cod', name: 'Dobírka' },
  ]
  payment = 'invoice'

  addresses: any[]
  address: any

  note: string

  constructor(
    protected core: CoreService,
    protected hsApi: HsApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fmtDatePipe: FmtDatePipe,
    private readonly translationService: TranslateService,
    private readonly notificationService: NotificationService,
  ) {
    // this.payments = [...this.core.lists.payments]
    // console.log(this.payments, this.core.lists) // todo payments on api for HS.. nope: hardcoded todo
    this.purchaseOrderId = +this.route.snapshot.params.id
    const selectedProducts = this.router.getCurrentNavigation()?.extras?.state?.selectedProducts
    this.suppliers = this.core.lists.suppliers
    this.purchaseOrderInfo.supplier = this.suppliers.find(s => s.externalId === 'henryschein')?.id
    this.purchaseCurrencyFromCompany = this.core.services.user.user?.company?.currency
    if (this.purchaseCurrencyFromCompany?.id === 'EUR') {
      this.freeShippingThreshold = FREE_SHIPPING_THRESHOLD_EUR
      this.shippingPrice = SHIPPING_PRICE_EUR
    }
    this.customerCode = this.core.services.user.user?.company?.externalId
    this.fetchAddresses()
    const po = this.core.getPurchaseOrderInProgress()
    if (po?.poInfo) {
      this.purchaseOrder = po.purchaseOrder
      this.purchaseOrderInfo = po.poInfo
      this.productList = po.productList
      this.calcSums()
    }
    if (this.purchaseOrderId) {
      this.fetch(this.purchaseOrderId).then(() => (this.objectForChangesCheck = JSON.stringify(this.productList)))
      this.isCreationModeEnabled = false
      this.isEditModeEnabled = false
      // if (!this.isCreationModeEnabled) this.filter(1)
      this.canEdit = this.core.services.user.hasRole('ROLE_PURCHASE_ORDER_UPDATE')
    } else if (selectedProducts) {
      this.productList.push(...selectedProducts)
      this.purchaseOrderInfo.depot = this.router.getCurrentNavigation().extras.state.depot
      this.loading = false
    } else {
      this.loading = false
    }
    if (!this.purchaseOrderId && this.productList.length === 0) {
      this.notificationService.error(this.translationService.instant('purchaseOrder.error.emptyCart'))
      this.router.navigate(['/purchase-order-hs'])
    }
  }

  hasUnsavedDataChanges(): boolean {
    // return this.objectForChangesCheck !== JSON.stringify(this.productList)
    try {
      if (
        (this.objectForChangesCheck?.length > 3 &&
          JSON.parse(this.objectForChangesCheck)?.length !== this.productList?.length) ||
        (!this.objectForChangesCheck && this.productList?.length)
      ) {
        return true
      }
    } catch (e) {
      console.log(e, this.objectForChangesCheck)
    }
    return false
  }

  public async fetch(id: number) {
    try {
      this.purchaseOrder = await this.core.services.purchase.getById(id, SchemaUtil.purchaseOrderDetail)
    } catch (e) {
      console.error('Failed to load purchase order:', e)
      this.purchaseOrder = null
    }
    if (!this.purchaseOrder) {
      this.productList = []
      return
    }
    this.setAdditionalInfo(this.purchaseOrder)
    this.purchaseCurrencyFromCompany = this.core.services.user.user?.company?.currency
    this.productList = (this.purchaseOrder.items || []).map(value => {
      const customData = typeof value.customData === 'string' ? JSON.parse(value.customData) : value.customData
      // already-saved orders must always use the stored vatHs rate, not the current product.vat
      const vat = this.hsApi.resolveVat(customData?.vatHs, value.product?.vat)
      const coefficient = this.hsApi.getVatHsCoefficient(customData?.vatHs, value.product?.vat)
      return (({
        id,
        quantity,
        quantityIn,
        purchasePrice,
        purchasePriceAll,
        currency,
        product,
        note,
        status,
        externalReference,
      }) => ({
        id,
        product: product?.id,
        quantity,
        quantityIn,
        purchasePrice,
        purchasePriceWithVat: roundNum(purchasePrice / coefficient),
        purchasePriceAll,
        purchasePriceAllWithVat: roundNum((purchasePrice / coefficient) * quantity),
        currency: this.purchaseCurrencyFromCompany,
        name: product.name,
        fullName: product.fullName,
        mainImage: product.mainImage,
        code: product.code,
        ean: product.ean,
        note,
        externalReference,
        status,
        vat,
        isValid: true,
        tags: product.tags || [],
        packs: product.packs || [],
        quantityAvailable: product.quantityAvailable,
        discount: 0, // active discount in percent
        purchasePriceWithDiscount: roundNum(purchasePrice / coefficient),
        discounts: {}, // all discounts for reference
        externalId: product.externalId,
        customData,
      }))(value)
    })
    this.calcSums()
  }

  private setAdditionalInfo(purchaseOrder: PurchaseOrder) {
    this.purchaseOrderInfo = (({
      supplier,
      depot,
      tags,
      externalId,
      externalReference,
      note,
      privateNote,
      created,
      createdBy,
      dateEstimatedArrival,
    }) => ({
      supplier: supplier.id,
      depot: depot.id,
      note: note,
      privateNote: privateNote,
      dateEstimatedArrival: this.fmtDatePipe.transform(dateEstimatedArrival, 'yyyy-MM-dd'),
      tags: tags?.map(tags => tags.id),
      externalId: externalId,
      externalReference,
      createdBy: createdBy?.name || 'AUTO',
      created: this.fmtDatePipe.transform(created, 'yyyy-MM-dd'),
    }))(purchaseOrder)
  }

  getThumbUrl(image: File): string {
    return imageGetThumbUrl(image)
  }

  calcSums() {
    this.discountsSum = 0
    this.priceSum = this.productList
      .map(p => {
        if (p.discount > 0) {
          return p.purchasePriceAllWithDiscount
        } else {
          return p.purchasePriceAll
        }
      })
      .reduce((a, b) => a + b, 0)
    this.priceWithVatSum = this.productList
      .map(p => {
        if (p.discount > 0) {
          this.discountsSum += p.purchasePriceAllWithVat * (p.discount / 100)
          return p.purchasePriceAllWithVatAndDiscount
        } else {
          return p.purchasePriceAllWithVat
        }
      })
      .reduce((a, b) => a + b, 0)
    const isEligibleForFreeShipping =
      this.priceWithVatSum >= this.freeShippingThreshold ||
      (this.purchaseOrder?.id > 0 && !this.purchaseOrder?.createdBy)
    const shippingPrice = this.purchaseCurrencyFromCompany?.id === 'EUR' ? SHIPPING_PRICE_EUR : SHIPPING_PRICE_CZK
    this.shippingPrice = isEligibleForFreeShipping ? 0 : shippingPrice
    this.hasUnsavedChanges = true
  }

  export(type: string) {
    this.core.services.download.downloadDocumentAndShowInNewWindow(this.purchaseOrder[type])
  }

  private collectInfo() {
    const common: PurchaseOrderInfo | any = {
      supplier: this.purchaseOrderInfo.supplier,
      depot: this.purchaseOrderInfo.depot,
      note: this.purchaseOrderInfo.note,
      externalReference: this.purchaseOrderInfo.externalReference,
      customData: {
        address: this.address?.address ? this.address.address : this.address,
        payment: this.payment,
        shippingPrice: this.shippingPrice,
      },
      items: this.productList.map(value => {
        return {
          product: value.product,
          quantity: value.quantity,
          purchasePrice: roundNum(value.purchasePriceWithDiscount),
          note: value.note || '',
          currency: this.purchaseCurrencyFromCompany.id,
          externalId: value.externalId || '',
          customData: {
            discount: value.discount,
            discounts: value.discounts,
            purchasePriceWithoutDiscount: roundNum(value.purchasePrice),
            purchasePriceWithVatWithoutDiscount: roundNum(value.purchasePriceWithVat),
            vatHs: value.customData?.vatHs,
          },
        }
      }),
    }
    return this.isCreationModeEnabled ? common : { ...common, id: this.purchaseOrderId }
  }

  navigateBack() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/purchase-order-hs'])
    }
  }

  async fetchAddresses() {
    const res = await this.hsApi.getHsAddresses()
    this.addresses = res?.map(a => ({
      id: a.id,
      name: [a.name, a.street, a.houseNumber, a.zip, a.city].filter(Boolean).join(', '),
      address: a,
    }))
    this.addresses.push({
      name: '---.purchaseOrder.address.create', // divider + new btn
      id: 0,
      address: {},
    })

    // Preselect address based on depot from customOptions
    const customOptions = this.core.getCustomOptions()
    const depotId = this.purchaseOrderInfo?.depot
    if (depotId) {
      const depotSetting = customOptions?.automaticOrdersSettings?.depots?.find(
        (d: any) => String(d.id) === String(depotId),
      )
      if (depotSetting?.addressId) {
        const found = this.addresses.find((a: any) => String(a.id) === String(depotSetting.addressId))
        if (found) {
          this.address = found
        }
      }
    }
  }

  onAddressSelected(address: any) {
    console.log('aaa', address) // todo wtf po druhy select createAddress to nevynulluje adresu
    if (address.id === 0) {
      this.address = null
      this.modalAddressCreate.showChildModal()
    } else {
      this.address = address
    }
  }

  onAddressCreated(address: any) {
    const a = {
      id: address.id,
      name: [address.name, address.street, address.houseNumber, address.zip, address.city].filter(Boolean).join(', '),
      address,
    }
    this.addresses.splice(this.addresses.length - 1, 0, a)
    this.onAddressSelected(a)
  }

  async finalizePurchaseOrder() {
    // console.log(this.purchaseOrderInfo, this.productList, this.address, this.note, this.payment)
    if (!this.address) {
      this.notificationService.warning(`Není vybrána adresa`)
      return
    }
    if (!this.purchaseOrderInfo.depot) {
      this.notificationService.warning(`Není vybrán sklad`)
      return
    }
    if (!this.purchaseOrderInfo.supplier) {
      this.notificationService.warning(`Není vybrán dodavatel`)
      return
    }
    const po = this.collectInfo()
    po.customData = JSON.stringify(po.customData)
    po.items.forEach((poi, i) => {
      po.items[i].customData = JSON.stringify(poi.customData)
    })
    // console.log('final stringified: ', { ...po })
    if (this.isCreationModeEnabled && this.purchaseOrder?.status?.id !== 'draft') {
      const result = await this.core.services.purchase.create(po, { id: null })
      if (!result?.id) {
        this.notificationService.error(this.translationService.instant('purchaseOrder.create.error'))
        return
      }
      this.hasUnsavedChanges = false
      this.isEditModeEnabled = false
      this.objectForChangesCheck = JSON.stringify(this.productList)
      this.core.setPurchaseOrderInProgress(null)
      this.router.navigate(['/purchase-order-hs', result.id], {})
      this.notificationService.success(`Naskladňovací objednávka byla úspěšně vytvořena.`)
    } else {
      // console.log('update', po)
      const result = await this.core.services.purchase.update({
        ...po,
        id: this.purchaseOrder?.id,
        status: { id: 'new' },
      }) // todo status new neni platna hodnota????WTF
      this.notificationService.success(`Naskladňovací objednávka byla úspěšně aktualizována.`)
      await this.fetch(this.purchaseOrder?.id)
      this.hasUnsavedChanges = false
      this.isEditModeEnabled = false
      this.objectForChangesCheck = JSON.stringify(this.productList)
      this.router.navigate(['/purchase-order-hs', result.id], {})
    }
  }
}

type PurchaseOrderInfo = {
  supplier: number
  depot: number
  note: string
  privateNote: string
  dateEstimatedArrival: string
  tags: Array<number>
  reference: string
  externalId: string
  externalReference: string
  createdBy: string
  created: string
  customData: any
}
